<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { type ILibrary, REQUIRMENTTYPE, REQUIRMENPARAGRAPHSTYLE } from '@/utils/libsMeta'
import settingService from '@/services/ide/settings/setting.service'

import { useIdeStore } from '@/stores/ide.store'
import { useProjectManager } from '@/stores/projectManager.store'
import { useField, useForm } from 'vee-validate'
import { string } from 'yup'
import { IDEVIEWTABS, IDEVIEWTABS_MOBILE } from '@/utils/tabs'
import { TAB_BUTTON_POSITION, TAB_ICONS_NAMES } from '@/utils/tabs'
import excuteLiberaryIcon from '@/assets/images/ide/folder-down.svg'
import stack from '@/assets/images/ide/stack.svg'
import deleteIcon from '@/assets/images/ide/delete.svg'
import infoIcon from '@/assets/images/ide/info-circle.svg'
import TabCompTitleBar from '@/components/ide/ide/TabCompTitleBar.vue'
import Button from '@/components/atoms/button/index.vue'

const props = defineProps({
  tabPosition: {
    type: String,
    required: false,
    default: TAB_BUTTON_POSITION.BOTTOM
  }
})

const ideStore = useIdeStore()
const projectManagerStore = useProjectManager()
const library = ref<ILibrary>(ideStore.ideMeta?.library as ILibrary)

const { errors, handleSubmit, resetForm } = useForm()
const { value: lib, errorMessage: libraryError } = useField('library', string().required())
const isFullScreen = computed(() => {
  return ideStore.isFullScreen
})
const isShowLibs = computed(() => {
  return ideStore.libraries.length > 0
})
const isLibs = computed(() => {
  return ideStore.libraries || []
})
const isManuallyInstall = computed(() => {
  return ideStore.ideMeta?.library?.manuallyInstall || false
})
const isCode = computed(() => {
  return ideStore.ideMeta?.library?.code || ''
})
const canSubmit = computed(() => {
  return Object.keys(errors.value).length === 0
})
const isCurrentMobileTab = computed(() => {
  return ideStore.currentMobileTab == IDEVIEWTABS_MOBILE.LIBRARIES
})
const disableCollabAccess = computed(() => {
  return projectManagerStore.projectPermissionWithId?.readOnly
})

/**
 * remove the library
 * @param lib The library name
 */
const removeLib = (lib: string) => {
  settingService.removelibraries(lib)
}

const onSubmit = handleSubmit(async (values) => {
  settingService.addlibraries(values.library)
  resetForm()
})

onMounted(async () => {
  watch(
    () => ideStore.ideMeta?.library,
    (newVal) => {
      library.value = newVal as ILibrary
    }
  )
})
</script>

<template>
  <div
    class="surface-secondary-base relative flex h-full w-full flex-col p-2 lg:p-0"
    :id="`${IDEVIEWTABS.LIBRARIES}-main`"
  >
    <TabCompTitleBar
      v-if="ideStore.currentMobileTab == null && props.tabPosition == TAB_BUTTON_POSITION.MAXIMIZE"
      titleName="External libraries"
      :icon="excuteLiberaryIcon"
      :tabPosition="props.tabPosition"
      :tabName="TAB_ICONS_NAMES.EXTERNAL_LIBRARIES"
    />

    <div class="relative h-full w-full overflow-auto">
      <div
        :class="[
          'flex w-full flex-col gap-8 sm:gap-5 sm:p-0 sm:pb-8 lg:px-2 lg:py-2',
          {
            ' w-full lg:flex-col ': !isFullScreen,
            'lg:flex-col ':
              props.tabPosition == TAB_BUTTON_POSITION.RIGHT ||
              props.tabPosition == TAB_BUTTON_POSITION.MAXIMIZE
          },
          { 'sm:pl-2': props.tabPosition === TAB_BUTTON_POSITION.BOTTOM }
        ]"
      >
        <div
          :class="[
            'flex h-full w-full flex-col gap-5',
            {
              'min-w-full max-w-md': isFullScreen
            }
          ]"
        >
          <div class="flex h-full w-full flex-col items-center">
            <div class="h-full w-full">
              <div class="flex h-full w-full flex-col gap-2">
                <form
                  v-if="!isManuallyInstall"
                  @submit.prevent="onSubmit"
                  class="flex w-full flex-col gap-2"
                >
                  <label class="text-high text-sm font-medium">
                    {{
                      library.header
                        ? `${library.header}`
                        : `Add ${library.package} from ${library.library} Library`
                    }}
                  </label>
                  <div
                    class="flex w-full flex-col gap-2 sm:flex-row"
                    :class="[
                      {
                        'sm:flex-col': props.tabPosition == TAB_BUTTON_POSITION.RIGHT
                      }
                    ]"
                  >
                    <div class="flex w-full overflow-hidden rounded-r-lg">
                      <input
                        data-testid="libField"
                        class="stroke-primary-default w-full truncate rounded-l-lg border border-new-neutral-200 p-2 text-black outline-none placeholder:text-sm placeholder:font-light"
                        :class="{ 'cursor-not-allowed': disableCollabAccess }"
                        :disabled="disableCollabAccess"
                        :placeholder="library.placeholder"
                        v-model="lib"
                      />
                      <Button
                        variant="filled"
                        :disabled="!canSubmit || disableCollabAccess"
                        class="mx-auto w-fit whitespace-nowrap rounded-none"
                        type="submit"
                      >
                        Add library
                      </Button>
                    </div>

                    <p
                      v-if="isCurrentMobileTab"
                      :class="['p-xsmall error text-left', { hidden: !libraryError }]"
                    >
                      {{ libraryError }}
                    </p>
                  </div>
                  <p
                    v-if="!isCurrentMobileTab"
                    :class="['p-xsmall error text-left', { hidden: !libraryError }]"
                  >
                    {{ libraryError }}
                  </p>
                </form>
                <div class="surface-secondary-header flex flex-col gap-3 rounded-md p-3">
                  <div class="text-mid text-xs font-medium">Instructions to follow:</div>
                  <div
                    v-for="(requrements, index) of library.requirments"
                    :key="index"
                    class="text-low flex items-start justify-start gap-2 pl-1 font-light"
                  >
                    <img :src="infoIcon" class="h-4 w-4" />
                    <span class="p-xsmall">
                      <span v-for="(requrement, index) of requrements" :key="index">
                        <span v-if="requrement.type === REQUIRMENTTYPE.LINK">
                          <a
                            :class="[
                              {
                                'text-mid font-medium':
                                  requrement.content.style === REQUIRMENPARAGRAPHSTYLE.BOLD
                              },
                              {
                                italic: requrement.content.style === REQUIRMENPARAGRAPHSTYLE.ITALIC
                              },
                              {
                                underline:
                                  requrement.content.style === REQUIRMENPARAGRAPHSTYLE.UNDERLINE
                              },
                              {
                                'line-through':
                                  requrement.content.style === REQUIRMENPARAGRAPHSTYLE.STRIKETHROUGH
                              }
                            ]"
                            :href="requrement.content.text"
                            rel="nofollow"
                            target="_blank"
                          >
                            <Button class="p-0 text-xs" variant="link">
                              {{ requrement.content.text }}</Button
                            >
                          </a>
                        </span>
                        <span
                          v-else-if="requrement.type === REQUIRMENTTYPE.PARAGRAPH"
                          :class="[
                            {
                              'text-mid font-medium':
                                requrement.content.style === REQUIRMENPARAGRAPHSTYLE.BOLD
                            },
                            {
                              italic: requrement.content.style === REQUIRMENPARAGRAPHSTYLE.ITALIC
                            },
                            {
                              underline:
                                requrement.content.style === REQUIRMENPARAGRAPHSTYLE.UNDERLINE
                            },
                            {
                              'line-through':
                                requrement.content.style === REQUIRMENPARAGRAPHSTYLE.STRIKETHROUGH
                            }
                          ]"
                        >
                          {{ requrement.content.text }}
                        </span>
                      </span>
                      <span>.</span>
                    </span>
                  </div>
                  <div v-if="isCode" class="p-xsmall flex flex-col gap-2">
                    <span class="font-bold">Example:</span>
                    <textarea
                      class="p-xsmall editor block h-36 w-full resize-none border-none outline-none"
                      :value="isCode"
                      readonly
                    >
                    </textarea>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="!isManuallyInstall"
          :class="[
            ' flex h-full w-full flex-col gap-3 sm:gap-5',
            {
              'min-w-full max-w-md pb-2': isFullScreen
            }
          ]"
        >
          <div class="text-high pl-2 text-sm font-medium leading-6 sm:pl-4">
            External libraries added
          </div>
          <div
            :class="[
              'h-full max-h-[25vh] overflow-y-auto rounded ',
              {
                'sm:max-h-max': !isFullScreen
              },
              {
                'pb-2 sm:max-h-[30vh] sm:p-5': isFullScreen
              }
            ]"
          >
            <ul v-if="isShowLibs" class="h-full">
              <li
                v-for="lib in isLibs"
                :key="lib"
                class="text-button-secondary background-primary mx-1 my-2 flex items-center justify-between gap-5 rounded px-2 py-3 sm:m-2"
              >
                <div class="flex gap-3">
                  <img :src="stack" class="h-[18px] w-[18px]" />
                  <p class="text-high truncate text-xs font-medium">{{ lib }}</p>
                </div>
                <button @click="removeLib(lib)">
                  <img :src="deleteIcon" class="h-3 w-3" />
                </button>
              </li>
            </ul>
            <p
              v-else
              class="surface-secondary-base text-low m-2 rounded-md p-3 text-left text-xs font-light"
            >
              No libraries added
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
